import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAIChat } from '../../hooks/useAIChat';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { useTranslation } from 'react-i18next';
import { getAITools, AIToolInfo, AICapabilityGroup } from '../../api/AI/chat';
import Icon, { IconName } from '../AppIcon';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogTitle } from '../ui/Dialog';
import { cn } from '../../utils/cn';
import { formatUtcReset, hoursUntilReset, isQuotaLow } from '../../utils/aiQuota';

interface UserRootState {
  user: { role: string };
}

/**
 * Capability buckets in display order, each with a friendly heading key and a
 * section icon. Tools whose group isn't listed here fall into a "More" bucket.
 */
const CAPABILITY_GROUPS: { key: AICapabilityGroup; labelKey: string; icon: IconName }[] = [
  { key: 'leave', labelKey: 'ai.assistant.group.leave', icon: 'Palmtree' },
  { key: 'attendance', labelKey: 'ai.assistant.group.attendance', icon: 'Clock' },
  { key: 'profile', labelKey: 'ai.assistant.group.profile', icon: 'User' },
  { key: 'team', labelKey: 'ai.assistant.group.team', icon: 'Users' },
  { key: 'navigation', labelKey: 'ai.assistant.group.navigation', icon: 'Compass' },
];

/**
 * ── THE FAB IS A PERMANENT OVERLAY ON EVERY PAGE, AND IT WAS EATING THEIR CLICKS ────────────────
 *
 * The launcher is `fixed bottom-6 end-6` with `z-[1300]`, so it owns a 64px disc in the bottom-END
 * corner of the viewport on EVERY authenticated screen. Any page whose primary action docks in that
 * same corner (a `flex justify-end` action row that happens to sit at the bottom of the viewport, or
 * a `sticky bottom-0` action bar, which is there PERMANENTLY) hands its click to the FAB instead.
 * Measured on `/users/add` in Arabic: a genuine pointer click on Submit timed out with the FAB
 * intercepting. It is not an RTL bug — RTL is just where it was caught, because `justify-end` and
 * `end-6` resolve to the same edge in both directions.
 *
 * Fixing the one form would have been a lie: the FAB is the thing that moves across every page, so
 * the FAB is what has to get out of the way. On each layout change it hit-tests the nine points of
 * its own resting footprint; if a clickable element is underneath, it lifts just far enough to clear
 * that element's top edge.
 *
 * ⚠️ THE HIT-TEST USES THE RESTING FOOTPRINT, NEVER THE CURRENT ONE. Measuring where the FAB
 * actually is would oscillate: lifted → nothing underneath → drop → collision → lift, once per
 * scroll event. Reading the resting box makes the offset a pure function of the page layout, so it
 * settles.
 *
 * Escape hatch: `data-fab-ignore` on an element opts it out of collision (use it for decorative
 * full-bleed click targets that would otherwise pin the FAB permanently).
 */
/** Matches `bottom-6` on the dock. Keep in lockstep with the class below. */
const FAB_BASE_INSET_PX = 24;
/** Breathing room kept between the FAB and whatever it would otherwise cover. */
const FAB_CLEARANCE_PX = 12;
/** A lift beyond this fraction of the viewport means something is wrong; stay put instead. */
const FAB_MAX_LIFT_RATIO = 0.4;
/**
 * `data-fab-obstacle` opts a NON-interactive element into the same dodge. The hit-test was written
 * for clickables (it was fixing stolen clicks), so a purely READ-ONLY band under the corner — the
 * app footer, a card's last line at 390px — was never seen and the FAB sat on top of it. Marking
 * such a band reuses the machinery already here rather than adding a second measurement path.
 */
const FAB_COLLIDABLE =
  'button, a[href], input, select, textarea, [role="button"], [role="link"], [role="tab"], [data-fab-obstacle]';

const AIAssistant: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const role = useSelector((state: UserRootState) => state.user.role);
  const isHrOrAdmin = role === 'Hr' || role === 'Admin';
  const {
    isOpen,
    isLoading,
    isConfigured,
    statusMessage,
    currentSession,
    toggleOpen,
    close,
    sendMessage,
    clearChat,
    messagesEndRef,
    quota,
  } = useAIChat();

  const [toolsDialogOpen, setToolsDialogOpen] = useState(false);
  const [tools, setTools] = useState<AIToolInfo[]>([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const fabDockRef = useRef<HTMLDivElement>(null);
  const [fabLift, setFabLift] = useState(0);

  // See the FAB collision note above the component.
  useEffect(() => {
    if (isOpen) {
      setFabLift(0);
      return undefined;
    }

    let frame = 0;

    const measure = () => {
      frame = 0;
      const node = fabDockRef.current;
      if (!node || typeof document.elementsFromPoint !== 'function') return;

      const rect = node.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      // The RESTING box: where the dock sits with no lift applied.
      const viewportHeight = window.innerHeight;
      const restingBottom = viewportHeight - FAB_BASE_INSET_PX;
      const restingTop = restingBottom - rect.height;

      const xs = [rect.left + 6, (rect.left + rect.right) / 2, rect.right - 6];
      const ys = [restingTop + 6, (restingTop + restingBottom) / 2, restingBottom - 6];

      let highestObstacleTop = Number.POSITIVE_INFINITY;

      for (const x of xs) {
        for (const y of ys) {
          if (y < 0 || y > viewportHeight) continue;
          for (const el of document.elementsFromPoint(x, y)) {
            // The FAB itself is on top at rest; look past it, not at it.
            if (el === node || node.contains(el)) continue;
            const hit = (el as HTMLElement).closest?.(FAB_COLLIDABLE) as HTMLElement | null;
            if (
              hit &&
              !node.contains(hit) &&
              !hit.hasAttribute('data-fab-ignore') &&
              !(hit as HTMLButtonElement).disabled
            ) {
              const hitRect = hit.getBoundingClientRect();
              if (hitRect.top < highestObstacleTop) highestObstacleTop = hitRect.top;
            }
            // Only the TOPMOST element at a point can receive the click, so stop here.
            break;
          }
        }
      }

      if (!Number.isFinite(highestObstacleTop)) {
        setFabLift(0);
        return;
      }

      const needed = Math.round(
        viewportHeight - highestObstacleTop + FAB_CLEARANCE_PX - FAB_BASE_INSET_PX,
      );
      const maxLift = Math.round(viewportHeight * FAB_MAX_LIFT_RATIO);
      setFabLift(needed <= 0 ? 0 : Math.min(needed, maxLift));
    };

    const schedule = () => {
      if (frame || document.hidden) return;
      frame = window.requestAnimationFrame(measure);
    };

    schedule();
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
    // Route changes and async content swap what is under the corner without a scroll or a resize.
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
    };
  }, [isOpen, isConfigured]);

  // Keep panel inert when closed so off-screen elements are not reachable via Tab
  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.inert = !isOpen;
    }
  }, [isOpen]);

  // Load tools when the capability panel opens
  useEffect(() => {
    if (toolsDialogOpen && tools.length === 0) {
      loadTools();
    }
  }, [toolsDialogOpen]);

  const loadTools = async () => {
    setToolsLoading(true);
    try {
      const result = await getAITools();
      if (result.status === 200 && result.payload) {
        setTools(result.payload.tools);
      }
    } catch (error) {
      console.error('Failed to load tools:', error);
    } finally {
      setToolsLoading(false);
    }
  };

  // Send an example prompt through the normal chat path, then reveal the conversation.
  const runExample = (prompt: string) => {
    setToolsDialogOpen(false);
    sendMessage(prompt);
  };

  const goToConsultant = () => {
    setToolsDialogOpen(false);
    close();
    navigate('/ai-consultant');
  };

  // CO-225 — allowance spent. The refusal is explained here, in place, instead of letting the user
  // type a question and only then meet a 429 toast.
  const quotaExhausted = !!quota && !quota.exempt && quota.limit > 0 && quota.used >= quota.limit;

  const knownGroupKeys = new Set<AICapabilityGroup>(CAPABILITY_GROUPS.map((g) => g.key));
  const leftoverTools = tools.filter((tool) => !knownGroupKeys.has(tool.capabilityGroup));

  const renderTool = (tool: AIToolInfo) => (
    <div key={tool.name} className="flex gap-3">
      <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon name={tool.icon as IconName} size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{t(tool.displayNameKey)}</p>
        <p className="text-xs text-muted-foreground">{t(tool.descriptionKey)}</p>
        {tool.examplePromptKeys.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {tool.examplePromptKeys.map((exampleKey) => {
              const prompt = t(exampleKey);
              return (
                <button
                  key={exampleKey}
                  type="button"
                  onClick={() => runExample(prompt)}
                  className="rounded-full border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {prompt}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderGroup = (labelKey: string, icon: IconName, groupTools: AIToolInfo[]) => {
    if (groupTools.length === 0) return null;
    return (
      <section key={labelKey} className="space-y-3">
        <div className="flex items-center gap-2">
          <Icon name={icon} size={14} className="text-muted-foreground" />
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t(labelKey)}
          </h3>
        </div>
        <div className="space-y-4 ps-1">{groupTools.map(renderTool)}</div>
      </section>
    );
  };

  // Don't render if not configured and not already open
  if (!isConfigured && !isOpen) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button, on a dock that lifts it clear of whatever it would cover.
          The lift rides on `transform` (compositor-only) rather than on `bottom`, so the dodge
          never triggers layout. */}
      {!isOpen && (
        <div
          ref={fabDockRef}
          className="fixed bottom-6 end-6 z-[1300] transition-transform duration-200 ease-out motion-reduce:transition-none"
          style={fabLift ? { transform: `translateY(-${fabLift}px)` } : undefined}
        >
          <button
            onClick={toggleOpen}
            className="w-16 h-16 rounded-full bg-primary/20 hover:bg-primary/30 shadow-lg hover:shadow-xl transition-all duration-300 opacity-60 hover:opacity-100 hover:scale-105 flex items-center justify-center focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={t('ai.assistant.open')}
          >
            <img src="/logo192.png" alt="" className="w-10 h-10 object-contain" />
          </button>
        </div>
      )}

      {/* Floating Chat Panel — always rendered for animation; inert when closed */}
      <div
        ref={panelRef}
        aria-hidden={!isOpen}
        className={cn(
          'fixed end-6 bottom-6 z-[1300] flex flex-col bg-card rounded-2xl shadow-[-4px_0_24px_rgba(0,0,0,0.12)] transition-all duration-300 ease-in-out w-[min(400px,calc(100vw-3rem))] h-[min(600px,calc(100vh-6rem))]',
          isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border rounded-t-2xl">
          <div className="flex items-center gap-2">
            <img src="/logo192.png" alt="" className="w-8 h-8 object-contain" />
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('ai.assistant.title')}</h2>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setToolsDialogOpen(true)}
                  className="flex items-center gap-1 text-xs text-primary hover:bg-primary/5 rounded px-1 py-0.5 transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <Icon name="Sparkles" size={12} />
                  <span>{t('ai.assistant.capabilities')}</span>
                </button>
                {/* CO-225 — remaining daily AI requests. Rendered as quiet meta text beside the
                    capabilities link, not as a badge: it is background information until it runs
                    low, at which point the single colour shift is the only thing that changes.
                    Hidden outright for exempt users (Operator / BYO key / AI off) — a fabricated
                    "0 of 0" would be worse than nothing. */}
                {quota && !quota.exempt && quota.limit > 0 && (
                  <>
                    <span aria-hidden="true" className="text-xs text-muted-foreground/40">·</span>
                    <span
                      className={cn(
                        'text-xs tabular-nums',
                        isQuotaLow(quota.used, quota.limit) ? 'text-warning' : 'text-muted-foreground',
                      )}
                      title={t('ai.assistant.quotaResetHint', {
                        resetsAt: formatUtcReset(quota.resetsAtUtc, i18n.language),
                        hours: hoursUntilReset(quota.resetsAtUtc),
                      })}
                    >
                      {t('ai.assistant.quotaRemaining', {
                        remaining: Math.max(0, quota.limit - quota.used),
                        limit: quota.limit,
                      })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={clearChat}
              disabled={currentSession.messages.length === 0}
              className="p-2 rounded-lg hover:bg-muted transition-colors disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={t('ai.assistant.newConversation')}
            >
              <Icon name="Trash2" size={18} className="text-muted-foreground" />
            </button>
            <button
              onClick={close}
              className="p-2 rounded-lg hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={t('ai.assistant.close')}
            >
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto flex flex-col p-4 bg-muted/30">
          {/* Welcome message */}
          {currentSession.messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center gap-4 px-6">
              <img
                src="/logo192.png"
                alt=""
                className="w-16 h-16 object-contain opacity-80"
              />
              <h3 className="text-lg font-semibold text-foreground">
                {t('ai.assistant.welcomeTitle')}
              </h3>
              <p className="text-sm text-muted-foreground max-w-[300px] leading-relaxed">
                {t('ai.assistant.welcomeSubtitle')}
              </p>

              {/* Quick suggestions */}
              <div className="flex flex-col gap-2 w-full mt-4">
                <span className="text-xs text-muted-foreground/60 font-semibold">
                  {t('ai.assistant.tryAsking')}
                </span>
                {[
                  t('ai.assistant.suggestion.leaveBalance'),
                  t('ai.assistant.suggestion.attendanceWeek'),
                  t('ai.assistant.suggestion.pendingLeave'),
                ].map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => sendMessage(suggestion)}
                    className="p-3 bg-card border border-border rounded-lg text-start transition-all duration-200 hover:border-primary hover:bg-primary/5 hover:translate-x-1 rtl:hover:-translate-x-1 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <span className="text-sm text-foreground">{suggestion}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Chat messages */}
          {currentSession.messages.map((message) => (
            <ChatMessage key={message.id} message={message} onNavigate={close} />
          ))}

          {/* Typing indicator */}
          {isLoading && (
            <div className="flex gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <img src="/logo192.png" alt="" className="w-[18px] h-[18px] object-contain" />
              </div>
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-muted">
                {[0, 0.2, 0.4].map((delay, i) => (
                  <div
                    key={i}
                    className="w-2 h-2 rounded-full bg-muted-foreground/40"
                    style={{ animation: 'typing 1.4s infinite', animationDelay: `${delay}s` }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Auto-scroll anchor */}
          <div ref={messagesEndRef} />
        </div>

        <div className="border-t border-border" />

        {/* Input */}
        <ChatInput
          onSendMessage={sendMessage}
          disabled={!isConfigured || quotaExhausted}
          isLoading={isLoading}
          placeholder={
            !isConfigured
              ? t('ai.assistant.notConfiguredPlaceholder')
              : quotaExhausted
                ? t('ai.assistant.quotaExhaustedPlaceholder')
                : t('ai.assistant.inputPlaceholder')
          }
        />

        {/* Status message */}
        {!isConfigured && (
          <div className="mx-4 mb-4 p-3 rounded-lg bg-warning/10 border border-warning/20 text-sm text-warning-foreground">
            <div className="flex items-center gap-2">
              <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0" />
              <span>{statusMessage || t('ai.assistant.notConfigured')}</span>
            </div>
          </div>
        )}

        {/* CO-225 — daily allowance spent. Names the reset in UTC and adds the hours, because UTC
            midnight is 3am for a Riyadh user and "midnight" would be actively misleading. */}
        {isConfigured && quotaExhausted && quota && (
          <div className="mx-4 mb-4 p-3 rounded-lg bg-warning/10 border border-warning/20 text-sm text-warning-foreground">
            <div className="flex items-start gap-2">
              <Icon name="Clock" size={16} className="text-warning flex-shrink-0 mt-0.5" />
              <span>
                {t('ai.assistant.quotaExhausted', {
                  limit: quota.limit,
                  resetsAt: formatUtcReset(quota.resetsAtUtc, i18n.language),
                  hours: hoursUntilReset(quota.resetsAtUtc),
                })}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Capability panel — "What I can help with" */}
      <Dialog open={toolsDialogOpen} onOpenChange={setToolsDialogOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon name="Sparkles" size={18} className="text-primary" />
              {t('ai.assistant.capabilities')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {toolsLoading ? (
              <p className="text-center text-muted-foreground py-4">{t('ai.assistant.loading')}</p>
            ) : tools.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">{t('ai.assistant.noCapabilities')}</p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">{t('ai.assistant.capabilitiesIntro')}</p>
                <div className="mt-5 space-y-6">
                  {CAPABILITY_GROUPS.map((g) =>
                    renderGroup(
                      g.labelKey,
                      g.icon,
                      tools.filter((tool) => tool.capabilityGroup === g.key)
                    )
                  )}
                  {renderGroup('ai.assistant.group.more', 'Sparkles', leftoverTools)}
                </div>
              </>
            )}

            {/* D3 clarifier: distinguish this self-service assistant from the
                HR/Admin AI Implementation Consultant. CTA is HR/Admin only. */}
            <div className="mt-6 rounded-lg border border-border bg-muted/40 p-3">
              <div className="flex gap-2.5">
                <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="space-y-2 text-xs leading-relaxed">
                  <p className="text-muted-foreground">{t('ai.assistant.about')}</p>
                  {isHrOrAdmin && (
                    <>
                      <p className="text-muted-foreground">{t('ai.assistant.consultantDistinction')}</p>
                      <button
                        type="button"
                        onClick={goToConsultant}
                        className="inline-flex items-center gap-1.5 rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <Icon name="Sparkles" size={14} />
                        {t('ai.assistant.consultantCta')}
                        <Icon name="ArrowRight" size={13} className="rtl:rotate-180" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AIAssistant;
